import { useState, useMemo } from "react";
import { getDeviceId, setCustomDeviceName } from "@/lib/deviceSession";
import { supabase } from "@/api/base44Client";
import { logActivity } from "@/utils/activityLogger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Laptop,
  Smartphone,
  Tablet,
  Pencil,
  LogOut,
  ShieldCheck,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Radio,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";

export default function ConnectedDevicesSection({ member, onUpdate }) {
  const currentDeviceId = useMemo(() => getDeviceId(), []);

  const [renameTarget, setRenameTarget] = useState(null);
  const [newDeviceName, setNewDeviceName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const [showDisconnectAllModal, setShowDisconnectAllModal] = useState(false);
  const [isDisconnectingAll, setIsDisconnectingAll] = useState(false);

  const activeSessions = useMemo(() => {
    if (!member?.active_sessions || !Array.isArray(member.active_sessions)) {
      return [];
    }
    // Urutkan perangkat saat ini paling atas, lalu urutkan berdasarkan last_active terbaru
    return [...member.active_sessions].sort((a, b) => {
      if (a.device_id === currentDeviceId) return -1;
      if (b.device_id === currentDeviceId) return 1;
      const tA = a.last_active ? new Date(a.last_active).getTime() : 0;
      const tB = b.last_active ? new Date(b.last_active).getTime() : 0;
      return tB - tA;
    });
  }, [member?.active_sessions, currentDeviceId]);

  const otherSessionsCount = activeSessions.filter((s) => s.device_id !== currentDeviceId).length;

  const getDeviceIcon = (type) => {
    switch (type) {
      case "mobile":
        return Smartphone;
      case "tablet":
        return Tablet;
      default:
        return Laptop;
    }
  };

  const formatLastActive = (dateStr, isCurrent) => {
    if (isCurrent) return "Aktif saat ini";
    if (!dateStr) return "Baru saja";
    try {
      const d = new Date(dateStr);
      const diffMinutes = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMinutes < 2) return "Aktif baru saja";
      if (diffMinutes < 60) return `Aktif ${diffMinutes} menit lalu`;
      const diffHours = Math.round(diffMinutes / 60);
      if (diffHours < 24) return `Aktif ${diffHours} jam lalu`;
      return d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Baru saja";
    }
  };

  const formatLoginTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };

  // Buka modal Rename
  const handleOpenRename = (session) => {
    setRenameTarget(session);
    setNewDeviceName(session.device_name || "");
  };

  // Simpan Rename Perangkat
  const handleSaveRename = async () => {
    if (!renameTarget || !newDeviceName.trim()) {
      toast.error("Nama perangkat tidak boleh kosong.");
      return;
    }

    setIsRenaming(true);
    try {
      const updatedSessions = (member?.active_sessions || []).map((s) => {
        if (s.device_id === renameTarget.device_id) {
          return { ...s, device_name: newDeviceName.trim() };
        }
        return s;
      });

      // Update di Supabase TeamMember
      const { error } = await supabase
        .from("TeamMember")
        .update({
          data: {
            ...member,
            active_sessions: updatedSessions,
          },
          updated_date: new Date().toISOString(),
        })
        .eq("id", member.id);

      if (error) throw error;

      // Jika perangkat saat ini, simpan juga di localStorage lokal
      if (renameTarget.device_id === currentDeviceId) {
        setCustomDeviceName(newDeviceName.trim());
      }

      // Log aktivitas ubah nama perangkat
      logActivity({
        portal: 'workspace',
        page: 'profile_security',
        action_type: 'device_rename',
        title: `Ubah Nama Perangkat: ${newDeviceName.trim()}`,
        details: `Mengubah nama perangkat dari "${renameTarget.device_name}" menjadi "${newDeviceName.trim()}" (ID: ${renameTarget.device_id})`,
        target_id: renameTarget.device_id,
        target_name: newDeviceName.trim(),
        user_name: member?.full_name || member?.name,
        user_role: member?.role,
      }).catch(console.error);

      toast.success("Nama perangkat berhasil diperbarui!");
      setRenameTarget(null);
      await onUpdate?.();
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengubah nama perangkat: " + err.message);
    } finally {
      setIsRenaming(false);
    }
  };

  // Putuskan Sesi Perangkat Tertentu (Logout Paksa)
  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget) return;

    setIsDisconnecting(true);
    try {
      const updatedSessions = (member?.active_sessions || []).filter(
        (s) => s.device_id !== disconnectTarget.device_id
      );

      const { error } = await supabase
        .from("TeamMember")
        .update({
          data: {
            ...member,
            active_sessions: updatedSessions,
          },
          updated_date: new Date().toISOString(),
        })
        .eq("id", member.id);

      if (error) throw error;

      // Log aktivitas putuskan sesi perangkat
      logActivity({
        portal: 'workspace',
        page: 'profile_security',
        action_type: 'security_logout',
        title: `Putuskan Sesi Perangkat: ${disconnectTarget.device_name}`,
        details: `Memutuskan sesi login perangkat "${disconnectTarget.device_name}" (ID: ${disconnectTarget.device_id}, Tipe: ${disconnectTarget.device_type || 'laptop'})`,
        target_id: disconnectTarget.device_id,
        target_name: disconnectTarget.device_name,
        user_name: member?.full_name || member?.name,
        user_role: member?.role,
      }).catch(console.error);

      toast.success(`Sesi perangkat "${disconnectTarget.device_name}" berhasil diputuskan!`);
      setDisconnectTarget(null);
      await onUpdate?.();
    } catch (err) {
      console.error(err);
      toast.error("Gagal memutuskan sesi: " + err.message);
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Putuskan Semua Perangkat Lain Sekaligus
  const handleConfirmDisconnectAll = async () => {
    setIsDisconnectingAll(true);
    try {
      const updatedSessions = (member?.active_sessions || []).filter(
        (s) => s.device_id === currentDeviceId
      );

      const { error } = await supabase
        .from("TeamMember")
        .update({
          data: {
            ...member,
            active_sessions: updatedSessions,
          },
          updated_date: new Date().toISOString(),
        })
        .eq("id", member.id);

      if (error) throw error;

      // Log aktivitas putuskan semua perangkat lain
      logActivity({
        portal: 'workspace',
        page: 'profile_security',
        action_type: 'security_logout',
        title: `Putuskan Semua Perangkat Lain`,
        details: `Memutuskan ${otherSessionsCount} perangkat lain yang terhubung dengan akun ${member?.full_name || member?.name}`,
        user_name: member?.full_name || member?.name,
        user_role: member?.role,
      }).catch(console.error);

      toast.success("Semua perangkat lain berhasil diputuskan!");
      setShowDisconnectAllModal(false);
      await onUpdate?.();
    } catch (err) {
      console.error(err);
      toast.error("Gagal memutuskan perangkat lain: " + err.message);
    } finally {
      setIsDisconnectingAll(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#8C6208]" />
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Perangkat yang Terhubung
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#8C6208]/10 text-[#8C6208] border border-[#8C6208]/20">
            {activeSessions.length} Perangkat Aktif
          </span>
        </div>
      </div>

      {/* Subtitle Deskripsi */}
      <div className="px-5 py-3 border-b border-border/60 bg-white/40">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Berikut adalah daftar perangkat yang sedang masuk menggunakan akun Anda. Anda dapat mengubah nama perangkat atau memutuskan sesi perangkat lain jika sudah tidak digunakan.
        </p>
      </div>

      {/* List Sesi Perangkat */}
      <div className="divide-y divide-border">
        {activeSessions.length === 0 ? (
          <div className="px-5 py-8 text-center text-muted-foreground text-sm">
            Belum ada data perangkat aktif yang tercatat.
          </div>
        ) : (
          activeSessions.map((session) => {
            const isCurrent = session.device_id === currentDeviceId;
            const Icon = getDeviceIcon(session.device_type);

            return (
              <div
                key={session.device_id}
                className={`p-4 sm:p-5 flex items-start sm:items-center justify-between gap-3 transition-colors ${
                  isCurrent ? "bg-emerald-50/30" : "hover:bg-muted/20"
                }`}
              >
                {/* Info Perangkat */}
                <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                      isCurrent
                        ? "bg-emerald-100/80 border-emerald-200 text-emerald-800"
                        : "bg-muted border-border text-muted-foreground"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800 truncate">
                        {session.device_name || "Perangkat Kantor"}
                      </span>

                      {isCurrent && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                          Perangkat Ini
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                      <span>{session.os || "OS"}</span>
                      <span>•</span>
                      <span>{session.browser || "Browser"}</span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                        <Clock className="w-3 h-3 opacity-60" />
                        {formatLastActive(session.last_active, isCurrent)}
                      </span>
                    </div>

                    {session.login_at && (
                      <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
                        <span>Masuk pertama: {formatLoginTime(session.login_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Aksi: Edit Nama & Putuskan */}
                <div className="flex items-center gap-1.5 shrink-0 self-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900 gap-1 rounded-lg"
                    onClick={() => handleOpenRename(session)}
                    title="Ubah nama perangkat"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Ubah Nama</span>
                  </Button>

                  {!isCurrent && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-2.5 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200 gap-1 rounded-lg transition-colors"
                      onClick={() => setDisconnectTarget(session)}
                      title="Logout paksa perangkat ini"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span>Putuskan</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Action Footer: Putuskan Semua Perangkat Lain */}
      {otherSessionsCount > 0 && (
        <div className="p-4 bg-muted/20 border-t border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Radio className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
            <span>Ada {otherSessionsCount} perangkat lain yang sedang login</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 gap-1.5 rounded-xl ml-auto"
            onClick={() => setShowDisconnectAllModal(true)}
          >
            <LogOut className="w-3.5 h-3.5" />
            Putuskan Semua Perangkat Lain
          </Button>
        </div>
      )}

      {/* Dialog Ubah Nama Perangkat */}
      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Pencil className="w-4 h-4 text-[#8C6208]" />
              Ubah Nama Perangkat
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Beri nama yang mudah Anda kenali untuk perangkat ini (misal: "Laptop Kantor Fadil", "PC Meja Produksi", "HP Pribadi").
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Nama Perangkat</Label>
              <Input
                value={newDeviceName}
                onChange={(e) => setNewDeviceName(e.target.value)}
                placeholder="Masukkan nama perangkat..."
                className="mt-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveRename();
                  }
                }}
              />
            </div>

            {renameTarget && (
              <div className="bg-muted/40 p-2.5 rounded-xl border border-border text-[11px] text-muted-foreground space-y-1">
                <p>
                  <strong className="text-slate-700">Sistem Operasi:</strong> {renameTarget.os}
                </p>
                <p>
                  <strong className="text-slate-700">Browser:</strong> {renameTarget.browser}
                </p>
                <p>
                  <strong className="text-slate-700">Device ID:</strong>{" "}
                  <code className="font-mono text-[10px]">{renameTarget.device_id}</code>
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setRenameTarget(null)}
              disabled={isRenaming}
            >
              Batal
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-[#8C6208] hover:bg-[#6a4a06] text-white"
              onClick={handleSaveRename}
              disabled={isRenaming || !newDeviceName.trim()}
            >
              {isRenaming ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Menyimpan...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Simpan Nama
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Konfirmasi Logout Paksa Satu Perangkat */}
      <Dialog open={!!disconnectTarget} onOpenChange={(open) => !open && setDisconnectTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-rose-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" />
              Putuskan Sesi Perangkat?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Apakah Anda yakin ingin mengeluarkan perangkat ini secara paksa? Perangkat tersebut akan otomatis ter-logout dan harus login ulang untuk dapat mengakses sistem.
            </DialogDescription>
          </DialogHeader>

          {disconnectTarget && (
            <div className="bg-rose-50/60 p-3 rounded-xl border border-rose-100 text-xs text-rose-900 space-y-1 my-1">
              <p className="font-bold">{disconnectTarget.device_name || "Perangkat"}</p>
              <p className="text-[11px] text-rose-700">
                {disconnectTarget.os} • {disconnectTarget.browser} • Terakhir aktif: {formatLastActive(disconnectTarget.last_active, false)}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDisconnectTarget(null)}
              disabled={isDisconnecting}
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirmDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Memutuskan...
                </>
              ) : (
                <>
                  <LogOut className="w-3.5 h-3.5 mr-1.5" /> Putuskan Sesi
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Konfirmasi Putuskan Semua Perangkat Lain */}
      <Dialog open={showDisconnectAllModal} onOpenChange={setShowDisconnectAllModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-rose-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" />
              Putuskan Semua Perangkat Lain?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Semua {otherSessionsCount} perangkat lain yang sedang login ke akun Anda akan langsung di-logout paksa. Hanya perangkat yang sedang Anda gunakan saat ini yang akan tetap aktif.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDisconnectAllModal(false)}
              disabled={isDisconnectingAll}
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirmDisconnectAll}
              disabled={isDisconnectingAll}
            >
              {isDisconnectingAll ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Memproses...
                </>
              ) : (
                <>
                  <LogOut className="w-3.5 h-3.5 mr-1.5" /> Ya, Putuskan Semua
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
